import { Cookie, CookieSetOptions } from 'universal-cookie'
import { runtimeConfig } from '@app/common/config'
import {
  AB_TEST_CAMPAIGNS,
  AB_TEST_DATA_VARIANT_COOKIE,
  AB_TEST_DATA_STEP_COOKIE,
  SESSION_ID_COOKIE_EXPIRATION_TIME,
  OBSOLETE_AB_TEST_VARIANT_COOKIE,
  OBSOLETE_CURRENT_BOOKING_FLOW_STEP,
  AB_TEST_VARIANTS_BY_CAMPAIGN,
  AB_TEST_VARIANT_ENFORCED
} from '@app/constants'
import { setBookingStep, setStepsNumber } from '@app/store/booking'
import { AbTestVariants, setAbTestVariant } from '@app/store/ui'
import { PreloadAction } from '@app/common/preload'
import { getRandomItem, isEmpty } from '@app/utils/common'
import { getBookingFlow } from '@app/ui/pages/Booking/UnifiedWorkflow/AbTestFlows'

/*
 * Returns the actions, but does not dispatch those.
 * Most be idempotent, might get called multiple times during initialization.
 * Called by fillStore() during server side rendering and by app.tsx during client side rendering.
 * Called by both when the use loads the app.
 * When the user uses backwards navigation from outside of the portal, only app.tsx will call it.
 */
export const fillStoreInitBookingFlow = (
  fullUrl: string,
  hostName: string,
  config: typeof runtimeConfig,
  cookies?: {
    readonly get: (name: string) => any
    readonly set: (name: string, value: Cookie, options?: CookieSetOptions) => void
    readonly remove: (name: string, options?: CookieSetOptions) => void
  }
) => {
  const actionsToProcess: PreloadAction[] = []
  const abTestCampaign = config.joinSproutTherapyConfig?.AB_TEST_IS_EXECUTING || ''

  // use a clean root domain to avoid duplicated cookies (e.g. created by autism.elemy.com)
  const cookieDomain = `${hostName.replace(/^www./, '') || ''}`

  // Remove old unneeded A/B test cookies (old cookies without campaign index in their name)
  if (cookies?.get(OBSOLETE_AB_TEST_VARIANT_COOKIE)) {
    cookies?.remove(OBSOLETE_AB_TEST_VARIANT_COOKIE)
  }

  // Remove old booking step cookies
  if (cookies?.get(OBSOLETE_CURRENT_BOOKING_FLOW_STEP)) {
    cookies?.remove(OBSOLETE_CURRENT_BOOKING_FLOW_STEP)
  }

  let abTestVariant: AbTestVariants | undefined

  // Current A/B testing campaign (current cookies with campaign index in their name)
  if (AB_TEST_CAMPAIGNS.includes(abTestCampaign)) {
    const abTestVariantCookieName = `${AB_TEST_DATA_VARIANT_COOKIE}${abTestCampaign}`
    const abTestVariantsByCampaign = AB_TEST_VARIANTS_BY_CAMPAIGN[abTestCampaign] || []

    if (!isEmpty(AB_TEST_VARIANT_ENFORCED)) {
      // use forced variant regardless of AB_TEST_VARIANTS_BY_CAMPAIGN (for development testing)
      abTestVariant = AB_TEST_VARIANT_ENFORCED
    } else {
      abTestVariant = Number(cookies?.get(abTestVariantCookieName))

      // validate that this variant is supported currently
      // (if campaign is incremented properly, this could not happen), see in constants / index.tsx
      if (!isEmpty(abTestVariant) && !abTestVariantsByCampaign.includes(abTestVariant)) {
        abTestVariant = undefined
      }
    }

    if (isEmpty(abTestVariant) && abTestVariantsByCampaign) {
      abTestVariant = getRandomItem(abTestVariantsByCampaign)!
    }

    // we should keep the same A/B test variant for the same user's session
    // so we will use the same expiration time
    const expirationDate = new Date(new Date().getTime() + SESSION_ID_COOKIE_EXPIRATION_TIME)

    cookies?.set(abTestVariantCookieName, abTestVariant, {
      domain: cookieDomain,
      path: '/',
      expires: expirationDate,
      httpOnly: false
    })
  }

  // returns the originalFlow (Control) by default
  const bookingFlow = getBookingFlow(abTestVariant)

  if (!isEmpty(abTestVariant)) {
    actionsToProcess.unshift(() => setAbTestVariant(abTestVariant!))
    actionsToProcess.unshift(() => setStepsNumber(bookingFlow.stepsNumber))

    // try to find previous booking step in workflow
    const stepCookieName = `${AB_TEST_DATA_STEP_COOKIE}${abTestVariant}`
    const stepIdFromCookie = cookies?.get(stepCookieName)

    const initialStep = bookingFlow.steps.find(t => t.stepId === stepIdFromCookie)
    if (initialStep) {
      actionsToProcess.unshift(() => setBookingStep(initialStep.stepId))
    }
  }

  // Update initial step information
  actionsToProcess.unshift(() => setStepsNumber(bookingFlow.stepsNumber))
  return actionsToProcess
}
